import type * as schemas from "@common/schemas/v2";
import * as models from "@common/models/v2";
import { BaseStep } from "./base";
import { openNewTab, openNewWindow, openSameTab } from "@/lib/window";
import { StepActions } from "./step";
import { ElementClicker } from "@/lib/element";
import { makeObservable, observable, override } from "mobx";
import { FlowFlexibility } from "@common/schemas";

export type ModalStep = MultipleChoiceModalStep;
export type ModalChoice =
  | FinishTourModalChoice
  | ProceedNextModalChoice
  | LinkModalChoice
  | SwitchTourModalChoice
  | SuspendTourModalChoice
  | TriggerClickModalChoice;

export class MultipleChoiceModalStep extends BaseStep {
  attributes: models.MultipleChoiceModalStepAttributes;
  actions: StepActions;
  choices!: ModalChoice[];
  variant?: string;
  isAnswered = false;
  allowProceed = false;

  constructor(step: schemas.Step, actions: StepActions, flowFlexibility: FlowFlexibility) {
    super(step, actions, flowFlexibility);
    this.attributes = new models.MultipleChoiceModalStepAttributes(
      step.attributes as schemas.MultipleChoiceModalStepAttributes
    );
    this.actions = actions;
    this.variant = this.attributes.variant;

    this.initializeChoices(actions);

    makeObservable(this, {
      isAnswered: observable,
      isBlocking: override,
      allowProceed: observable,
    });
  }

  initializeChoices(actions: StepActions) {
    if (this.variant === "theydo") {
      this.choices = this.theydoChoices(actions);
      return;
    }

    this.choices = ModalChoiceFactory.createList(this.attributes.choices, actions);
  }

  theydoChoices(actions: StepActions) {
    return [
      new ProceedNextModalChoice(
        {
          id: "notNow",
          type: "proceedNext",
          text: "Not now",
          buttonVariant: "primary",
        },
        actions
      ),
      new TriggerClickModalChoice(
        {
          id: "unlock",
          type: "triggerClick",
          text: "Show me",
          selector: '//*[@id="app"]/div/div[1]/nav/div/div[3]/div/div/div/span/button',
          buttonVariant: "primary",
        },
        actions
      ),
    ];
  }

  async run() {
    this.isAnswered = false;
    this.allowProceed = false;
  }

  async answer(choiceId: string) {
    const choice = this.choices.find((c) => c.id === choiceId);
    if (!choice) {
      return;
    }
    super.emitter.emit("modalAnswered", { choice });
    this.isAnswered = true;

    const { allowProceed } = await choice.execute();
    this.allowProceed = allowProceed;
  }

  get isBlocking() {
    return !this.allowProceed;
  }

  get isBlockingStepType() {
    return true;
  }

  get modal() {
    if (this.isAnswered) {
      return null;
    }

    return {
      title: this.attributes.title,
      modalContent: this.attributes.modalContent,
      choices: this.choices,
      description: this.attributes.description,
      variant: this.attributes.variant,
    };
  }
}

export class FinishTourModalChoice {
  id: string;
  type = "finishTour";
  text: string;
  buttonVariant: schemas.ChoiceButtonVariant;
  actions: StepActions;

  constructor(choice: schemas.ModalFinishTourChoice, actions: StepActions) {
    const finishTourChoice = new models.FinishTourModalChoice(choice);
    this.id = finishTourChoice.id;
    this.text = finishTourChoice.text;
    this.buttonVariant = finishTourChoice.buttonVariant;
    this.actions = actions;
  }

  execute() {
    this.actions.finishTour();
    return { allowProceed: false };
  }
}

export class ProceedNextModalChoice {
  id: string;
  type = "proceedNext";
  text: string;
  buttonVariant: schemas.ChoiceButtonVariant;
  actions: StepActions;

  constructor(choice: schemas.ProceedNextChoice, actions: StepActions) {
    const proceedNextChoice = new models.ProceedNextModalChoice(choice);
    this.id = proceedNextChoice.id;
    this.text = proceedNextChoice.text;
    this.buttonVariant = proceedNextChoice.buttonVariant;
    this.actions = actions;
  }

  execute() {
    return { allowProceed: true };
  }
}

export class ContactSupportModalChoice {
  id: string;
  type = "contactSupport";
  text: string;
  url: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.ContactSupportChoice, actions: StepActions) {
    const contactSupportChoice = new models.ContactSupportModalChoice(choice);
    this.id = contactSupportChoice.id;
    this.url = contactSupportChoice.url;
    this.text = contactSupportChoice.text;
    this.actions = actions;
    this.buttonVariant = contactSupportChoice.buttonVariant;
  }

  execute() {
    openNewTab(this.url);
    return { allowProceed: true };
  }
}

export class LinkModalChoice {
  id: string;
  text: string;
  windowType: schemas.LinkChoiceWindowType;
  url: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant;

  constructor(choice: schemas.LinkChoice, actions: StepActions) {
    const linkChoice = new models.LinkChoice(choice);

    this.id = linkChoice.id;
    this.text = linkChoice.text;
    this.windowType = linkChoice.windowType;
    this.url = linkChoice.url;
    this.actions = actions;
    this.buttonVariant = linkChoice.buttonVariant;
  }

  execute() {
    switch (this.windowType) {
      case "newTab":
        openNewTab(this.url);
        break;
      case "sameTab":
        openSameTab(this.url);
        break;
      case "newWindow":
        openNewWindow(this.url);
        break;
      default:
        openNewTab(this.url);
        break;
    }
    return { allowProceed: true };
  }
}

export class SwitchTourModalChoice {
  id: string;
  type = "switchTour";
  text: string;
  guideId: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";
  constructor(choice: schemas.SwitchTourChoice, actions: StepActions) {
    const switchTourChoice = new models.SwitchTourChoice(choice);
    this.id = switchTourChoice.id;
    this.text = switchTourChoice.text;
    this.guideId = switchTourChoice.guideId;
    this.actions = actions;
    this.buttonVariant = switchTourChoice.buttonVariant;
  }

  execute() {
    this.actions.switchTour(this.guideId);
    return { allowProceed: false };
  }
}

export class TriggerClickModalChoice {
  id: string;
  type = "triggerClick";
  text: string;
  selector: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.TriggerClickChoice, actions: StepActions) {
    const triggerClickChoice = new models.TriggerClickModalChoice(choice);
    this.id = triggerClickChoice.id;
    this.text = triggerClickChoice.text;
    this.selector = triggerClickChoice.selector;
    this.actions = actions;
    this.buttonVariant = triggerClickChoice.buttonVariant;
  }

  async execute() {
    const elementClicker = new ElementClicker();
    await elementClicker.clickElement(this.selector);
    return { allowProceed: true };
  }
}

export class SuspendTourModalChoice {
  id: string;
  type = "suspendTour";
  text: string;
  actions: StepActions;
  buttonVariant: schemas.ChoiceButtonVariant = "primary";

  constructor(choice: schemas.SuspendTourChoice, actions: StepActions) {
    const suspendTourChoice = new models.SuspendTourModalChoice(choice);
    this.id = suspendTourChoice.id;
    this.text = suspendTourChoice.text;
    this.actions = actions;
    this.buttonVariant = suspendTourChoice.buttonVariant;
  }

  execute() {
    this.actions.suspendTour();
    return { allowProceed: false };
  }
}

export class ModalFactory {
  static create(step: schemas.ModalStep, actions: StepActions, flowFlexibility: FlowFlexibility): ModalStep {
    switch (step.attributes.questionType) {
      case "multipleChoice":
        return new MultipleChoiceModalStep(step, actions, flowFlexibility);
      default:
        throw new Error(`Unknown modal type: ${step.attributes.type}`);
    }
  }
}

export class ModalChoiceFactory {
  static create(choice: schemas.ModalChoice, actions: StepActions): ModalChoice {
    switch (choice.type) {
      case "finishTour":
        return new FinishTourModalChoice(choice as schemas.ModalFinishTourChoice, actions);
      case "proceedNext":
        return new ProceedNextModalChoice(choice as schemas.ModalProceedNextChoice, actions);
      case "link":
        return new LinkModalChoice(choice as schemas.ModalLinkChoice, actions);
      case "contactSupport":
        return new ContactSupportModalChoice(choice as schemas.ModalContactSupportChoice, actions);
      case "switchTour":
        return new SwitchTourModalChoice(choice as schemas.ModalSwitchTourChoice, actions);
      case "suspendTour":
        return new SuspendTourModalChoice(choice as schemas.ModalSuspendTourChoice, actions);
      case "triggerClick":
        return new TriggerClickModalChoice(choice as schemas.ModalTriggerClickChoice, actions);
      default:
        throw new Error(`Unknown modal choice type: ${(choice as schemas.ModalChoice).type}`);
    }
  }
  static createList(choices: schemas.ModalChoice[], actions: StepActions): ModalChoice[] {
    return choices?.length
      ? choices.map((choice) => this.create(choice, actions))
      : [this.createDefaultChoice(actions)];
  }

  private static createDefaultChoice(actions: StepActions): ModalChoice {
    return new ProceedNextModalChoice(
      {
        id: "defaultChoice",
        type: "proceedNext",
        text: "Continue",
        buttonVariant: "primary",
      },
      actions
    );
  }
}
